import { Badge } from "./badge";

/**
 * Bestellzeile (Musterkatalog M-075, Design-Guide `listen-karte.md`): ein Gericht mit Preis, Allergenzeile, Frist-Hinweis
 * und einem Zustand rechts (Bestellen, Bestellt mit Stornieren, Frist vorbei, Abwesend). Gleiche Zeilenform wie
 * `ListenZeile` (Trennlinie, Titel `text-sm font-medium`, Meta in `text-xs`), aber ohne Symbol-Kachel und ohne Zeilenlink:
 * die Bedienelemente rechts sind eigenständig (kein Anker um die ganze Zeile). Auf schmalen Breiten steht die Aktion
 * unter dem Text (nichts schrumpft den Gerichtsnamen auf eine Spalte).
 *
 * Rein darstellend: `aktion` ist ein Platz für die bedienbaren Bausteine des Aufrufers (Knopf, Badge); die Rechte- und
 * Fristprüfung gehört in die Server-Action und in die Entscheidung des Aufrufers, was angeboten wird (r110). Die
 * Allergenzeile ist Pflicht (LMIV Art. 44), „keine Angaben hinterlegt“ steht beim Aufrufer als Text und heißt nie „frei“.
 *
 * Abweichung AW-091: Eigenbau aus Zeilenform und Bausteinen, kein Katalogblock 1:1 (siehe `docs/ui-preline/abweichungen.json`).
 *
 * Abweichung AW-095 (2), Startseite v2: `allergenListe` zeigt die Allergene als Chips (neutrales `Badge`, VOLLE Namen, KS3)
 * mit dem Vorwort „Allergene“; eine leere Liste heißt „keine Angaben hinterlegt“ als gestrichelter Chip und nie „frei von
 * Allergenen“. `allergene` (Textzeile) bleibt für den Speiseplan unverändert; sind beide gesetzt, gilt die Chip-Zeile.
 */
export function BestellZeile({
  titel,
  untertitel,
  abzeichen,
  allergene,
  allergenListe,
  hinweis,
  preis,
  aktion,
  meldung,
}: {
  titel: string;
  /** Beilage oder Zusatz hinter dem Titel, gedämpft. */
  untertitel?: string | null;
  /** Abzeichen direkt hinter dem Titel (Flags wie „vegetarisch“). */
  abzeichen?: React.ReactNode;
  /**
   * Vollständiger Text der Allergenzeile („Allergene: Gluten, Sellerie“); Pflicht bei Speisen. Entfällt nur bei der
   * Strichzeile (Snacks und Getränke, M-076, AW-092): dort führt das Sortiment keine Allergenangaben.
   */
  allergene?: string;
  /**
   * Allergene als Chips mit vollen Namen (Startseite v2, AW-095 (2)). Leere Liste = „keine Angaben hinterlegt“ (gestrichelt,
   * heißt nie „frei“). Pflicht bei Speisen, wenn `allergene` nicht gesetzt ist (LMIV Art. 44).
   */
  allergenListe?: string[];
  /** Zweite Meta-Zeile, z. B. „Bestellung bis heute 09:00 Uhr“. */
  hinweis?: string;
  /** Preis als fertiger Text („6,50 €“). */
  preis?: string;
  /** Zustand und Bedienelemente rechts. */
  aktion?: React.ReactNode;
  /** Satz am Feld (Ablehnung oder Fehlschlag), unter der Zeile; vom Aufrufer als `Alert` gesetzt. */
  meldung?: React.ReactNode;
}) {
  return (
    <>
      {/* Preline: data-display/data-display-sub-operational-activity-views/data-display-cat-list-groups/brand-logo-list */}
      <li className="py-3 border-b last:border-b-0 border-line-2">
        <div className="flex flex-col gap-y-2 sm:flex-row sm:items-start sm:gap-x-4">
          <div className="grow min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-sm font-medium text-foreground">{titel}</span>
              {untertitel && <span className="text-sm text-muted-foreground-1">· {untertitel}</span>}
              {abzeichen}
            </div>
            {allergenListe ? (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-muted-foreground-1">Allergene</span>
                {allergenListe.length > 0 ? (
                  allergenListe.map((n) => (
                    <Badge key={n} ton="neutral">
                      {n}
                    </Badge>
                  ))
                ) : (
                  <span className="py-0.5 px-2 inline-flex items-center rounded-full text-xs border border-dashed border-line-2 text-muted-foreground-1">keine Angaben hinterlegt</span>
                )}
              </div>
            ) : (
              allergene && <p className="mt-1 text-xs text-muted-foreground-1">{allergene}</p>
            )}
            {hinweis && <p className="text-xs text-muted-foreground-1">{hinweis}</p>}
          </div>
          {(preis || aktion) && (
            <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 sm:justify-end">
              {preis && <span className="text-sm font-semibold text-foreground tabular-nums">{preis}</span>}
              {aktion}
            </div>
          )}
        </div>
        {meldung && <div className="mt-2">{meldung}</div>}
      </li>
    </>
  );
}
